<template>
  <div class="sw-modal form-entry">
    <div class="sw-modal-content">
      <div class="sw-modal-heading">
        <div class="row">
          <div class="col-12">
            <h4><i class="fas fa-users"></i>Request Amendment</h4>
          </div>
        </div>
      </div>
      <div class="sw-modal-body">
        <div class="row">
          <div class="col-12">
            <p>Please be aware that changing the arrival or departure date may affect the total price for your stay
            and is also subject to availability and minimum stays. Additionally, increasing or decreasing the number
              of persons may affect the total price for your stay.</p>
            <p>Your host will follow up with you regarding availability and pricing.</p>
          </div>
        </div>
        <div class="row amend-booking-section">
          <div class="col-12">
            <label for="checkIn">Check-in & out</label><br/>
            <div class="input-group amend-booking__dates">
              <flat-pickr v-model="checkInOut" v-bind:config="flatPickrConfig" id="checkIn"></flat-pickr>
              <a title="Select date" data-toggle>
                <i class="fas fa-calendar-alt"></i>
              </a>
            </div>
          </div>
        </div>
        <div class="row amend-booking-section">
          <div class="col-12">
            <table class="booking-summary__summary-rooms">
              <thead>
                <th class="summary-rooms__name">{{getUnitTypeForDisplay}}</th>
                <th>Adult<span class="d-none d-sm-inline">s</span><br/><span class="text-note">({{establishment.rules.childCategory2AgeLimit}}+<span class="d-none d-sm-inline"> yrs</span>)</span></th>
                <th v-if="isChildrenCategory2Allowed">Child<span class="d-none d-sm-inline">ren</span><br/><span class="text-note">({{establishment.rules.childCategory2AgeFrom}} - {{establishment.rules.childCategory2AgeTo}}<span class="d-none d-sm-inline"> yrs</span>)</span></th>
                <th v-if="isChildrenCategory1Allowed">Child<span class="d-none d-sm-inline">ren</span><br/><span class="text-note">({{establishment.rules.childCategory1AgeFrom}} - {{establishment.rules.childCategory1AgeTo}}<span class="d-none d-sm-inline"> yrs</span>)</span></th>
              </thead>
              <tr v-for="(room, index) in copy.rooms" v-bind:key="index">
                <td class="summary-rooms__name">
                  <b>
                    <template v-if="establishment.preferences.showRoomTypeNameInsteadOfRoomName">
                      {{room.roomTypeName}}
                    </template>
                    <template v-else>
                      {{room.roomName}}
                    </template>
                  </b><br/>
                  <span class="text-note">
                    ({{getMaxOccupancyForRoom(room)}}
                    <template v-if="getMaxOccupancyForRoom(room) === 1">person</template>
                    <template v-else>people</template>
                    in {{getNumberOfBedroomsForRoom(room)}}
                    <template v-if="getNumberOfBedroomsForRoom(room) === 1">bedroom)</template>
                    <template v-else>bedrooms)</template>
                  </span>
                </td>
                <td>
                    <select v-model="room.numberOfAdults">
<!--                      <option value="0">0</option>-->
                      <option v-for="index in getMaxAdultsForRoom(room)" v-bind:key="index" v-bind:value="index">{{index}}</option>
                    </select>
                </td>
                <td>
                    <select v-model="room.numberOfChildren2" v-if="isChildrenCategory2Allowed(room)">
                      <option value="0">0</option>
                      <option v-for="index in getMaxChildrenCategory2ForRoom(room)" v-bind:key="index" v-bind:value="index">{{index}}</option>
                    </select>
                    <template v-else>
                      N/A
                    </template>
                </td>
                <td>
                    <select v-model="room.numberOfChildren1" v-if="isChildrenCategory1Allowed(room)">
                      <option value="0">0</option>
                      <option v-for="index in getMaxChildrenCategory1ForRoom(room)" v-bind:key="index" v-bind:value="index">{{index}}</option>
                    </select>
                    <template v-else>
                      N/A
                    </template>
                </td>
              </tr>
              <tr>
                <td v-show="showValidationError" colspan="4" class="valid-error text-center">
                  Please check the occupancy of each room
                </td>
              </tr>
            </table>
          </div>
        </div>
        <div class="sw-modal-actions">
          <div class="row">
            <div class="col-12">
              <Spinner v-show="isShowUpdatingServer" class="edit-guest-allocation__activity-spinner"/>
              <div v-show="!isShowUpdatingServer && updateFailed" class="edit-guest-allocation__save-fail">{{serverMessage}}</div>
              <div v-show="!isShowUpdatingServer && updateSucceeded" class="edit-guest-allocation__save-success">{{serverMessage}}</div>
            </div>
          </div>
          <div class="row">
            <div class="col-12">
              <template v-if="!updateSucceeded">
                <button v-on:click.stop="close()" class="btn btn-secondary">Cancel</button>
                <button v-on:click.stop="update()" class="btn btn-primary" v-bind:disabled="!isChanged">Request</button>
              </template>
              <template v-else>
                <button v-on:click.stop="refresh" class="btn btn-primary">Refresh</button>
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import dayjs from 'dayjs'
import FlatPickr from 'vue-flatpickr-component'
import 'flatpickr/dist/flatpickr.css'
import 'flatpickr/dist/themes/material_blue.css'
import Spinner from '../components/Spinner.vue'

export default {
  name: 'EditGuestAllocation',
  components: { Spinner, FlatPickr },
  props: {
    booking: Object,
    establishment: Object,
    displayCategoryForRoomTypes: String
  },
  data: function () {
    return {
      flatPickrConfig: {
        mode: 'range',
        altInput: true,
        altFormat: 'M j Y',
        dateFormat: 'Y-m-d',
        minDate: dayjs().startOf('day').toDate(),
        wrap: true
      },
      checkInOut: [dayjs(this.booking.arrival).toDate(), dayjs(this.booking.departure).toDate()],
      copy: {
        rooms: []
      },
      showValidationError: false,
      isShowUpdatingServer: false,
      updateSucceeded: false,
      updateFailed: false,
      serverMessage: ''
    }
  },
  computed: {
    getUnitTypeForDisplay: function () {
      switch (this.displayCategoryForRoomTypes) {
        case 'unit':
          return 'Unit'
        case 'property':
          return 'Property'
        case 'room':
        default:
          return 'Room type'
      }
    },
    isChanged: function () {
      const dates = this.getDatesArray(this.checkInOut)
      if (dates[0] !== this.booking.arrival || dates[1] !== this.booking.departure) {
        return true
      }
      for (let i = 0; i < this.copy.rooms.length; i++) {
        if (this.copy.rooms[i].numberOfAdults !== this.booking.rooms[i].numberOfAdults ||
          this.copy.rooms[i].numberOfChildren1 !== this.booking.rooms[i].numberOfChildren1 ||
          this.copy.rooms[i].numberOfChildren2 !== this.booking.rooms[i].numberOfChildren2) {
          return true
        }
      }
      return false
    }
  },
  methods: {
    isChildrenCategory1Allowed: function (room) {
      if (this.establishment.roomTypes) {
        for (const roomType of this.establishment.roomTypes) {
          if (room.externalRoomTypeId === roomType.externalId) {
            return roomType.childPolicy.allowChildCategory1
          }
        }
      }
      return true
    },
    isChildrenCategory2Allowed: function (room) {
      if (this.establishment.roomTypes) {
        for (const roomType of this.establishment.roomTypes) {
          if (room.externalRoomTypeId === roomType.externalId) {
            return roomType.childPolicy.allowChildCategory2
          }
        }
      }
      return true
    },
    getMaxAdultsForRoom: function (room) {
      if (this.establishment.roomTypes) {
        for (const roomType of this.establishment.roomTypes) {
          if (room.externalRoomTypeId === roomType.externalId) {
            return parseInt(roomType.maxAdults)
          }
        }
      }
      return 10
    },
    getMaxChildrenCategory1ForRoom: function (room) {
      if (this.establishment.roomTypes) {
        for (const roomType of this.establishment.roomTypes) {
          if (room.externalRoomTypeId === roomType.externalId) {
            return parseInt(roomType.maxOccupancy)
          }
        }
      }
      return 10
    },
    getMaxChildrenCategory2ForRoom: function (room) {
      if (this.establishment.roomTypes) {
        for (const roomType of this.establishment.roomTypes) {
          if (room.externalRoomTypeId === roomType.externalId) {
            return parseInt(roomType.maxOccupancy)
          }
        }
      }
      return 10
    },
    getMaxOccupancyForRoom: function (room) {
      if (this.establishment.roomTypes) {
        for (const roomType of this.establishment.roomTypes) {
          if (room.externalRoomTypeId === roomType.externalId) {
            return parseInt(roomType.maxOccupancy)
          }
        }
      }
      return 10
    },
    getNumberOfBedroomsForRoom: function (room) {
      if (this.establishment.roomTypes) {
        for (const roomType of this.establishment.roomTypes) {
          if (room.externalRoomTypeId === roomType.externalId) {
            return roomType.numberOfBedrooms
          }
        }
      }
      return 1
    },
    close: function () {
      this.$root.$emit('finished-editing-guest-allocation')
    },
    getDatesArray: function (datesValue) {
      // this is confusing, but is here because for vue-flatpickr-component to work with a date range,
      // checkInOut needs to start as an Array, but when dates are set using the flatpickr, the value
      // is changed to a String
      let dates = null
      if (Array.isArray(datesValue)) {
        dates = [dayjs(datesValue[0]).format('YYYY-MM-DD'), dayjs(datesValue[1]).format('YYYY-MM-DD')]
      } else {
        dates = datesValue.split(' to ')
      }
      return dates
    },
    update: function () {
      console.log('Validating')
      this.showValidationError = false

      for (const room of this.copy.rooms) {
        if ((parseInt(room.numberOfAdults) + parseInt(room.numberOfChildren1) + parseInt(room.numberOfChildren2)) > this.getMaxOccupancyForRoom(room)) {
          console.log('Too many people')
          this.showValidationError = true
          return
        }
      }

      this.updateFailed = false
      this.updateSucceeded = false
      this.isShowUpdatingServer = true

      // this is confusing, but is here because for vue-flatpickr-component to work with a date range,
      // checkInOut needs to start as an Array, but when dates are set using the flatpickr, the value
      // is changed to a String
      const dates = this.getDatesArray(this.checkInOut)

      const body = {
        reference: this.booking.reference,
        arrival: dates[0],
        departure: dates[1],
        deposit: this.booking.deposit,
        bookedBy: this.booking.bookedBy,
        billedTo: this.booking.billedTo,
        rooms: this.copy.rooms
      }

      this.$http.put('/guest/v1/guests/' + this.booking.bookedBy.reference + '/bookings/' + this.booking.reference, body).then(response => {
        for (let i = 0; i < this.booking.rooms.length; i++) {
          this.booking.rooms[i].numberOfAdults = this.copy.rooms[i].numberOfAdults
          this.booking.rooms[i].numberOfChildren1 = this.copy.rooms[i].numberOfChildren1
          this.booking.rooms[i].numberOfChildren2 = this.copy.rooms[i].numberOfChildren2
        }
        this.updateSucceeded = true
        this.serverMessage = 'The form has been updated. Please refresh to continue.'
      }).catch(error => {
        this.updateFailed = true
        if (error.response) {
          if (error.response.status === 500) {
            this.serverMessage = 'Problem saving your answer'
          } else {
            this.serverMessage = error.response.data.message
          }
        } else {
          this.serverMessage = 'Problem saving your answer'
        }
      }).finally(() => {
        this.isShowUpdatingServer = false
      })
    },
    refresh: function () {
      // window.location.reload()
      this.$root.$emit('finished-editing-guest-allocation')
      this.$root.$emit('response-form-updated')
    }
  },
  mounted () {
    for (let i = 0; i < this.booking.rooms.length; i++) {
      const roomCopy = { ...this.booking.rooms[i] }
      this.copy.rooms.push(roomCopy)
    }
    this.checkInOut = [dayjs(this.booking.arrival).toDate(), dayjs(this.booking.departure).toDate()]
  }
}
</script>

<style lang="scss" scoped>
  @import "../styles/components/modal";
  @import "../styles/components/booking_summary";
  @import "../styles/components/edit_guest_allocation";
</style>

<!--These are overrides for the flatpickr inputs -->
<style lang="scss">
  .form-control {
    background-color: #fff !important;
    border-color: inherit;
    display: inline-block;
    width: inherit;
    margin-bottom: 5px;
  }

  .flatpickr-calendar.arrowTop:after {
    border-bottom-color: $blue;
  }

  .flatpickr-calendar.arrowBottom:after {
    border-top-color: $blue;
  }

  .flatpickr-months .flatpickr-month,
  .flatpickr-current-month .flatpickr-monthDropdown-months,
  .flatpickr-current-month .flatpickr-monthDropdown-months .flatpickr-monthDropdown-month,
  .flatpickr-weekdays,
  span.flatpickr-weekday {
    background-color: $blue;
    color: $white;
  }

  .flatpickr-day.selected,
  .flatpickr-day.startRange,
  .flatpickr-day.endRange,
  .flatpickr-day.selected.inRange,
  .flatpickr-day.startRange.inRange,
  .flatpickr-day.endRange.inRange,
  .flatpickr-day.selected:focus,
  .flatpickr-day.startRange:focus,
  .flatpickr-day.endRange:focus,
  .flatpickr-day.selected:hover,
  .flatpickr-day.startRange:hover,
  .flatpickr-day.endRange:hover,
  .flatpickr-day.selected.prevMonthDay,
  .flatpickr-day.startRange.prevMonthDay,
  .flatpickr-day.endRange.prevMonthDay,
  .flatpickr-day.selected.nextMonthDay,
  .flatpickr-day.startRange.nextMonthDay,
  .flatpickr-day.endRange.nextMonthDay {
    background: $blue;
    border-color: $blue;
  }

  .flatpickr-day.selected.startRange + .endRange:not(:nth-child(7n+1)),
  .flatpickr-day.startRange.startRange + .endRange:not(:nth-child(7n+1)),
  .flatpickr-day.endRange.startRange + .endRange:not(:nth-child(7n+1)) {
    -webkit-box-shadow: -10px 0 0 $blue;
    box-shadow: -10px 0 0 $blue;
  }

  .flatpickr-day.week.selected {
    -webkit-box-shadow: -5px 0 0 $blue, 5px 0 0 $blue;
    box-shadow: -5px 0 0 $blue, 5px 0 0 $blue;
  }
</style>
